import { bem } from '@socialive/common';
import PropTypes from 'prop-types';
import { useLayoutEffect, useRef } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { useBounds, useRefAutoScrollToBottom, useTransitionName } from '../../../hooks';
import ChatMessage from './ChatMessage';
import './chatScrollPane.css';
import ChatUpdate from './ChatUpdate';
import { messageShape } from './shapes';

/*
#tailwind
chatScrollPane__topGradient chatScrollPane__topGradient--on
chatScrollPane__bottomGradient chatScrollPane__bottomGradient--on
chatScrollPane__message--enter chatScrollPane__message--enter-active
*/

const ChatScrollPane = ({ messages, visible }) => {
  const ref = useRef();
  const transitionName = useTransitionName(messages, 'chatScrollPane__message', 'chatScrollPane__messages');
  const transitionDuration = 250;

  const { scroll } = useBounds(ref);

  // NOTE: When chat first renders we want it to scroll to the bottom, but we don't want to have it use any scrolling
  // animation. React's initial rendering may happen asynchronously. We'll auto-scroll to bottom for the first 500 ms.
  useRefAutoScrollToBottom(ref, 500);

  useLayoutEffect(() => {
    if (visible) {
      ref.current.scrollTo({
        top: ref.current.scrollHeight,
        behavior: transitionName === 'chatScrollPane__messages' ? 'auto' : 'smooth',
      });
    }
  }, [messages, transitionName, visible]);

  return (
    <div ref={ref} className="chatScrollPane">
      <TransitionGroup component={null} appear={false} enter exit={false}>
        {messages.map((m) => (
          <CSSTransition key={m.id} timeout={transitionDuration} classNames="chatScrollPane__message-">
            <>{m.isEvent ? <ChatUpdate message={m} /> : <ChatMessage className="duration-op" message={m} />}</>
          </CSSTransition>
        ))}
      </TransitionGroup>
      <div className={bem`chatScrollPane__topGradient--${{ on: scroll.top }}`} />
      <div className={bem`chatScrollPane__bottomGradient--${{ on: scroll.bottom > 2 }}`} />
    </div>
  );
};

ChatScrollPane.propTypes = {
  messages: PropTypes.arrayOf(messageShape).isRequired,
  visible: PropTypes.bool.isRequired,
};

export default ChatScrollPane;
